import { useCallback, useContext, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDistanceToNow } from 'date-fns'
import { JourneyContext, ProjectContext } from '../../contexts'
import { Table, useSearchTableState } from '../../components'
import { useResolver } from '../../hooks'
import api from '../../api'
import { Alert, Button, Menu, MenuItem, Modal, Tag } from '../../ui'
import { Tooltip } from '../../ui/Tooltip'
import { camelToTitle } from '../../utils'
import { UserLookup } from '../users/UserLookup'
import { ModalProps } from '../../ui/Modal'
import { JourneyUserStep, StepError, User } from '../../types'
import { EditIcon } from '../../ui/icons'
import { DataTableCol } from '../../ui/DataTable'
import UserJourneyEntranceModal, { EntranceStep, typeVariants } from '../users/UserJourneyEntranceModal'
import { getJourneyStepUserName } from './JourneyStepUsersUtils'

interface GroupedError {
    message: string
    count: number
    last_seen: string
}

const groupErrors = (errors: StepError[]): GroupedError[] => {
    const map = new Map<string, GroupedError>()
    for (const error of errors) {
        const existing = map.get(error.message)
        if (existing) {
            existing.count++
            if (error.created_at > existing.last_seen) {
                existing.last_seen = error.created_at
            }
        } else {
            map.set(error.message, {
                message: error.message,
                count: 1,
                last_seen: error.created_at,
            })
        }
    }
    return [...map.values()]
}

interface StepUsersProps extends Omit<ModalProps, 'title'> {
    stepId: number
    stepType: string
}

export function JourneyStepUsers({ open, onClose, stepType, stepId }: StepUsersProps) {

    const { t } = useTranslation()
    const [{ id: projectId }] = useContext(ProjectContext)
    const [journey] = useContext(JourneyContext)
    const [isUserLookupOpen, setIsUserLookupOpen] = useState(false)
    const isEntrance = stepType === 'entrance'
    const options: Array<DataTableCol<JourneyUserStep>> = stepType === 'delay'
        ? [{
            key: 'options',
            title: t('options'),
            cell: ({ item: { user } }) => {
                if (user) {
                    return (
                        <Menu size="small">
                            <MenuItem icon={<EditIcon />} onClick={async () => await handleSkipDelay(user)}>
                                {t('skip_delay')}
                            </MenuItem>
                        </Menu>
                    )
                }
                return <></>
            },
        }]
        : []

    const state = useSearchTableState(useCallback(async params => await api.journeys.steps.searchUsers(projectId, journey.id, stepId, params), [projectId, journey.id, stepId]), {
        limit: 10,
        sort: 'id',
        direction: 'desc',
    })
    const [step, setStep] = useState<EntranceStep>()

    const errorsResolver = useCallback(async () => await api.journeys.steps.errors(projectId, journey.id, stepId), [projectId, journey.id, stepId])
    const [errors] = useResolver(errorsResolver)
    const groupedErrors = groupErrors(errors ?? [])

    const handleAddUserToEntrance = async (stepId: number, user: User) => {
        await api.journeys.users.trigger(projectId, journey.id, stepId, user)
        await state.reload()
    }

    const handleSkipDelay = async (user: User) => {
        await api.journeys.users.skipDelay(projectId, journey.id, user.id, stepId)
        await state.reload()
    }

    return <>
        <Modal
            open={open}
            onClose={onClose}
            title={t('users')}
            size="large"
            headerActions={
                isEntrance && <Button
                    size="small"
                    variant="primary"
                    onClick={() => setIsUserLookupOpen(true)}
                >{t('journey_add_user_to_entrance')}</Button>
            }
        >
            {groupedErrors.length > 0 && (
                <Alert
                    variant="error"
                    title={`${t('recent_errors')} (${(errors ?? []).length} most recent)`}
                    size="small"
                    body={
                        <ul className="step-error-list">
                            {groupedErrors.map(({ message, count, last_seen }) => (
                                <li key={message}>
                                    {message} — {count}x, {formatDistanceToNow(new Date(last_seen), { addSuffix: true })}
                                </li>
                            ))}
                        </ul>
                    }
                />
            )}
            <Table
                {...state}
                columns={[
                    {
                        key: 'name',
                        title: t('name'),
                        cell: ({ item }) => getJourneyStepUserName(item.user),
                    },
                    {
                        key: 'external_id',
                        title: t('external_id'),
                        cell: ({ item }) => item.user?.external_id ?? '-',
                    },
                    {
                        key: 'email',
                        title: t('email'),
                        cell: ({ item }) => item.user?.email ?? '-',
                    },
                    {
                        key: 'phone',
                        title: t('phone'),
                        cell: ({ item }) => item.user?.phone ?? '-',
                    },
                    {
                        key: 'type',
                        title: t('type'),
                        cell: ({ item }) => {
                            const tag = (
                                <Tag variant={typeVariants[item.type]}>
                                    {camelToTitle(item.type)}
                                </Tag>
                            )
                            if (item.type === 'error') {
                                const userError = item.user
                                    ? (errors ?? []).find(e => e.user_id === item.user!.id)
                                    : undefined
                                return (
                                    <Tooltip content={userError?.message ?? t('error')}>
                                        {tag}
                                    </Tooltip>
                                )
                            }
                            return tag
                        },
                    },
                    {
                        key: 'created_at',
                        title: t('step_date'),
                    },
                    {
                        key: 'delay_until',
                        title: t('delay_until'),
                    },
                    ...options,
                ]}
                onSelectRow={
                    isEntrance
                        ? ({ id, user }) => setStep({ entrance_id: id, user, journey })
                        : ({ user }) => {
                            if (user) {
                                window.open(`/projects/${projectId}/users/${user.id}`, '_blank')
                            }
                        }
                }
            />

            <UserLookup
                open={isUserLookupOpen}
                onClose={setIsUserLookupOpen}
                onSelected={async user => await handleAddUserToEntrance(stepId, user)} />

            <UserJourneyEntranceModal
                step={step}
                onClose={() => setStep(undefined)} />
        </Modal>
    </>
}
